import { Currency } from "@redotech/money/currencies";
import { safeParseEnum } from "@redotech/util/enum";
import {
  SegmentListMultipleItemMembershipOperator,
  SegmentListSingleItemMembershipOperator,
} from "../checkouts/checkout-tree";
import { ConjunctionMode } from "../segments/segment-types";
import { Team } from "../team";
import {
  OrderValueSegment,
  PostPurchaseCondition,
  PostPurchaseConditionBlock,
  PostPurchaseSegment,
  ProductCollectionSegment,
  ProductMetafieldsSegment,
  ProductOrderedSegment,
  ProductTagsSegment,
  SegmentablePostPurchaseCharacteristic,
} from "./post-purchase-flow";
import { PostPurchaseSegmentNumberOperator } from "./segment-types";

function getDefaultOrderValueCondition(
  team?: Team | undefined,
): OrderValueSegment {
  const shopCurrency = safeParseEnum(team?._shopify?.currency, Currency);
  return {
    segmentableCharacteristic:
      SegmentablePostPurchaseCharacteristic.ORDER_VALUE,
    amount: "50",
    currency: shopCurrency ?? Currency.USD,
    operator: PostPurchaseSegmentNumberOperator.GREATER_THAN,
  };
}

const defaultProductOrderedCondition: ProductOrderedSegment = {
  segmentableCharacteristic:
    SegmentablePostPurchaseCharacteristic.PRODUCTS_ORDERED,
  productIds: [],
  productListOperator: SegmentListSingleItemMembershipOperator.IN,
  cartOperator: SegmentListMultipleItemMembershipOperator.ANY_IN,
};

const defaultProductCollectionCondition: ProductCollectionSegment = {
  segmentableCharacteristic:
    SegmentablePostPurchaseCharacteristic.PRODUCT_COLLECTIONS,
  collectionIds: [],
  collectionListOperator: SegmentListMultipleItemMembershipOperator.ANY_IN,
  cartOperator: SegmentListMultipleItemMembershipOperator.ANY_IN,
};

const defaultProductMetafieldsCondition: ProductMetafieldsSegment = {
  segmentableCharacteristic:
    SegmentablePostPurchaseCharacteristic.PRODUCT_METAFIELDS,
  metafields: [],
  operator: SegmentListMultipleItemMembershipOperator.ANY_IN,
  cartOperator: SegmentListMultipleItemMembershipOperator.ANY_IN,
};

const defaultProductTagsCondition: ProductTagsSegment = {
  segmentableCharacteristic: SegmentablePostPurchaseCharacteristic.PRODUCT_TAGS,
  tagNames: [],
  tagListOperator: SegmentListMultipleItemMembershipOperator.ANY_IN,
  cartOperator: SegmentListMultipleItemMembershipOperator.ANY_IN,
};

export function getDefaultConditions(
  team?: Team | undefined,
): Record<SegmentablePostPurchaseCharacteristic, PostPurchaseCondition> {
  return {
    [SegmentablePostPurchaseCharacteristic.ORDER_VALUE]:
      getDefaultOrderValueCondition(team),
    [SegmentablePostPurchaseCharacteristic.PRODUCTS_ORDERED]:
      defaultProductOrderedCondition,
    [SegmentablePostPurchaseCharacteristic.PRODUCT_COLLECTIONS]:
      defaultProductCollectionCondition,
    [SegmentablePostPurchaseCharacteristic.PRODUCT_METAFIELDS]:
      defaultProductMetafieldsCondition,
    [SegmentablePostPurchaseCharacteristic.PRODUCT_TAGS]:
      defaultProductTagsCondition,
  };
}

export const defaultPostPurchaseSegment: PostPurchaseSegment = {
  conjunction: ConjunctionMode.AND,
  conditionBlocks: [],
};

export const defaultPostPurchaseSegmentConditionBlock: PostPurchaseConditionBlock =
  { conjunction: ConjunctionMode.AND, conditions: [] };
